import React, { useEffect, useState } from 'react'; // Added useState import
import { useDispatch, useSelector } from 'react-redux';
import { useViewport } from '../../context/ViewportContext';
import { 
  updateField, 
  resetForm, 
  submitContactForm,
  selectFormData,
  selectSubmitted,
  selectSubmitting,
  selectSubmitError,
  selectIsFormValid
} from '../../store/contactFormSlice';

import { useModelData } from '../../hooks/useModelData';
import PrivacyPolicyModal from './PrivacyPolicyModal';

const ContactForm = () => {
  const dispatch = useDispatch();
  const formData = useSelector(selectFormData);
  const submitted = useSelector(selectSubmitted);
  const submitting = useSelector(selectSubmitting);
  const submitError = useSelector(selectSubmitError);
  const isFormValid = useSelector(selectIsFormValid);
  
  const { isMobile } = useViewport();
  const {
    formData: formSpecData,
    selectedFormAdditionalRequirements,
    actions
  } = useModelData();
  
  // State for privacy policy
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [consentError, setConsentError] = useState('');
    
  // Reset form when component unmounts (optional)
  useEffect(() => {
    return () => {
      // Uncomment if you want to reset on unmount
      // dispatch(resetForm());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateField({ name, value }));
  };

  const handlePrivacyToggle = () => {
    setPrivacyAccepted(!privacyAccepted);
    setConsentError('');
  };

  const openPrivacyPolicy = () => {
    setShowPrivacyModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if privacy policy is accepted
    if (!privacyAccepted) {
      setConsentError('You must accept the privacy policy to submit this form');
      return;
    }

    if (isFormValid) {
      dispatch(submitContactForm(
        {
          ...formData, 
          partNumber: formSpecData.partNumber,
          additionalRequirements: selectedFormAdditionalRequirements,
        }
      ));

      // Reset additional message
      actions.setFormAdditionalRequirements("");
    }
  };

  if (submitted) {
    return (
      <div className="w-full">
        <div className="text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-green-800 mb-4">Thank You!</h2>
          <p className="text-green-700 mb-4">
            Your inquiry has been received. Our technical team will contact you within 24 hours.
          </p>
          <button
            onClick={() => {
              dispatch(resetForm());
              setPrivacyAccepted(false);
            }}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
          >
            Submit Another Inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
          {!isMobile && (
            <div className="border-b border-gray-200 bg-gray-50 p-6">
              <div className="flex items-center justify-center flex-col">
                <div className="text-center mb-1">
                  <h3 className="text-3xl font-bold border-b-4 border-blue-500 inline-block pb-1">
                    OCXO Product Inquiry
                  </h3>
                </div>
                <div className="text-center">
                  <p className="text-gray-600">
                    Request technical specifications, pricing for our high-precision OCXO products
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 m-4 rounded">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 px-4 mt-8">
            {/* Personal Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position/Title
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="RF Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="TechCorp Inc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="United States"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john.smith@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Special Requirements
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <textarea
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Project application, custom packaging, specific certifications, modified specifications..."
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Message or Questions</h2>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Please provide any additional information about your project or specific requirements..."
              />
            </div>

            {/* Privacy Policy Consent - SINGLE VERSION (removed duplicate) */}
            <div className="border-t pt-6 mt-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <input
                      id="privacy-consent"
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={handlePrivacyToggle}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="privacy-consent" className="text-sm text-gray-700">
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={openPrivacyPolicy}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Privacy Policy
                      </button>
                      . I consent to the collection and processing of my personal data for the purpose of responding to my inquiry. *
                    </label>
                    
                    {/* Privacy notice summary */}
                    <p className="text-xs text-gray-500 mt-2">
                      Your data will only be used to respond to your inquiry via email and will not be stored on our servers. 
                      We do not share your information with third parties.
                    </p>
                    
                    {/* Consent error message */}
                    {consentError && (
                      <p className="text-sm text-red-600 mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {consentError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <button
                type="submit"
                disabled={submitting || !isFormValid || !privacyAccepted}
                className={`
                  px-8 py-3 rounded-lg font-semibold transition-colors
                  ${submitting || !isFormValid || !privacyAccepted
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }
                `}
              >
                {submitting ? 'Sending...' : 'Submit Inquiry'}
              </button>
            </div>

            <div className="text-center text-sm text-gray-500 mt-4">
              <p>Our technical team will contact you to discuss your requirements.</p>
              <p>All inquiries are handled with strict confidentiality.</p>
            </div>
          </form>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        onAccept={() => {
          setPrivacyAccepted(true);
          setShowPrivacyModal(false);
          setConsentError('');
        }}
      />
    </>
  );
};

export default ContactForm;